import { useState, useEffect, useRef } from 'preact/hooks';

interface OtpDialogProps {
  /** Request ID returned by /api/restart/request */
  requestId: string;
  /** Service name for display */
  serviceName: string;
  /** Server name for display */
  serverName: string;
  /** Expiry timestamp (ISO string) */
  expiresAt: string;
  /** Called when user submits the code */
  onConfirm: (code: string) => Promise<void>;
  /** Called when dialog is dismissed */
  onClose: () => void;
  /** Called to resend OTP */
  onResend: () => Promise<void>;
  /** Error message from failed confirmation */
  error?: string;
}

export function OtpDialog({
  requestId,
  serviceName,
  serverName,
  expiresAt,
  onConfirm,
  onClose,
  onResend,
  error,
}: OtpDialogProps) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer
  useEffect(() => {
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleInput = (index: number, value: string) => {
    // Only accept digits
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Auto-advance to next field
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (digit && index === 5 && newDigits.every((d) => d !== '')) {
      handleSubmit(newDigits.join(''));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const newDigits = pasted.split('');
      setDigits(newDigits);
      inputRefs.current[5]?.focus();
      handleSubmit(pasted);
    }
  };

  const handleSubmit = async (code?: string) => {
    const finalCode = code ?? digits.join('');
    if (finalCode.length !== 6) return;
    setLoading(true);
    try {
      await onConfirm(finalCode);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend();
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const expired = remaining <= 0;

  return (
    <div class="otp-overlay" onClick={onClose}>
      <div class="otp-dialog" onClick={(e) => e.stopPropagation()}>
        <div class="otp-header">
          <h2>OTP-Bestätigung</h2>
          <button class="otp-close" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>

        <p class="otp-info">
          Code wurde per Telegram gesendet.
          <br />
          <strong>{serviceName}</strong> auf <strong>{serverName}</strong>
        </p>

        <div class="otp-inputs" onPaste={handlePaste}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onInput={(e) => handleInput(i, (e.target as HTMLInputElement).value)}
              onKeyDown={(e) => handleKeyDown(i, e as unknown as KeyboardEvent)}
              class={`otp-digit ${error ? 'otp-digit-error' : ''}`}
              disabled={loading || expired}
              aria-label={`Ziffer ${i + 1}`}
            />
          ))}
        </div>

        {error && <p class="otp-error">{error}</p>}

        <div class="otp-timer">
          {expired ? (
            <span class="otp-expired">Code abgelaufen</span>
          ) : (
            <span>Gültig noch: {formatTime(remaining)}</span>
          )}
        </div>

        <div class="otp-actions">
          <button
            class="btn btn-primary"
            onClick={() => handleSubmit()}
            disabled={loading || expired || digits.some((d) => !d)}
          >
            {loading ? 'Prüfe...' : 'Bestätigen'}
          </button>
          <button
            class="btn btn-link"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? 'Sende...' : 'Code erneut senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
